import { useCallback } from 'react';
import { updateSupplierFields } from '@/data/suppliersRepo';
import type { HistoryInfoType } from '@/types/types_history';
import { useCurrentUserSimple } from './useCurrentUserSimple';
import { applyHistoryToUpdate, type HistorizedSaveType } from './util_history';
import type { SaveStack } from './util_saveStack';

/**
 * Returns an `autosave(userAction, fields, currentHistory)` function that writes
 * changed fields of one supplier to Firestore, registering the operation on the
 * save stack. Mirrors useAutosavePartner.
 *
 * `fields` may name a single absence (`closurePeriods.{id}.reason`) rather than
 * the whole map, which is the point of storing them as one.
 */
export function useAutosaveSupplier(supplierId: string | undefined, saveStack: SaveStack) {
  const userSimple = useCurrentUserSimple();

  return useCallback(
    (
      userAction: string,
      fields: Record<string, unknown>,
      currentHistory: HistoryInfoType | undefined,
      isUndoStep = false,
    ): HistorizedSaveType => {
      if (!supplierId) {
        throw new Error(`Cannot autosave: no supplier id. action=[${userAction}]`);
      }
      const { updateObj, newHistory } = applyHistoryToUpdate(
        fields,
        userAction,
        currentHistory,
        userSimple,
        isUndoStep,
      );

      const opId = crypto.randomUUID();
      saveStack.pushToSaveStack({ id: opId, userAction });
      const saved = updateSupplierFields(supplierId, updateObj).then(() => {
        saveStack.removeFromSaveStack(opId);
      });

      return { newHistory, saved };
    },
    [supplierId, saveStack, userSimple],
  );
}
